/**
 * FeatureGate Component
 *
 * A component that conditionally renders content based on feature access.
 * Shows a fallback (upgrade prompt) when the user doesn't have access.
 */

import React from 'react';
import { useFeatureGate, FeatureId } from '../hooks/useFeatureAccess';
import { Lock, ArrowUpCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

interface FeatureGateProps {
  /**
   * The feature ID to check access for
   */
  featureId: FeatureId | string;

  /**
   * Content to render when access is granted
   */
  children: React.ReactNode;

  /**
   * Custom fallback content when access is denied.
   * Can be a ReactNode or a render function that receives upgradeRequired.
   * If not provided, a default upgrade prompt is shown.
   */
  fallback?: React.ReactNode | ((upgradeRequired: string | null) => React.ReactNode);

  /**
   * If true, shows a loading spinner while checking access
   * Default: true
   */
  showLoading?: boolean;

  /**
   * If true, renders nothing when locked (instead of fallback)
   * Useful for hiding features completely from lower tiers
   */
  hideWhenLocked?: boolean;

  /**
   * Custom loading component
   */
  loadingComponent?: React.ReactNode;

  /**
   * Callback when feature is locked
   */
  onLocked?: (featureId: string, upgradeRequired: string | null) => void;
}

/**
 * Default upgrade prompt shown when user doesn't have access
 */
function DefaultUpgradePrompt({
  featureId,
  upgradeRequired,
}: {
  featureId: string;
  upgradeRequired: string | null;
}) {
  const tierDisplayName = upgradeRequired
    ? upgradeRequired.charAt(0) + upgradeRequired.slice(1).toLowerCase()
    : 'a higher';

  return (
    <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
      <div className="flex justify-center mb-4">
        <div className="h-16 w-16 bg-indigo-50 rounded-full flex items-center justify-center">
          <Lock className="w-8 h-8 text-indigo-400" />
        </div>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Feature Locked</h3>
      <p className="text-gray-600 mb-6 max-w-sm mx-auto">
        This feature requires {tierDisplayName} tier or higher.
        Upgrade your plan to unlock this feature and more.
      </p>
      <Link
        to="/dashboard/billing"
        className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-medium transition-colors"
      >
        <ArrowUpCircle className="w-4 h-4" />
        Upgrade Plan
      </Link>
    </div>
  );
}

/**
 * Loading spinner shown while checking access
 */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center p-6">
      <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
    </div>
  );
}

/**
 * FeatureGate - Conditionally renders content based on feature access
 *
 * @example Basic usage
 * ```tsx
 * <FeatureGate featureId="deal_analyzer">
 *   <DealAnalyzer />
 * </FeatureGate>
 * ```
 *
 * @example With custom fallback
 * ```tsx
 * <FeatureGate
 *   featureId="csv_export"
 *   fallback={<CustomUpgradePrompt />}
 * >
 *   <ExportButton />
 * </FeatureGate>
 * ```
 *
 * @example Hide completely when locked
 * ```tsx
 * <FeatureGate featureId="beta_features" hideWhenLocked>
 *   <BetaFeatureButton />
 * </FeatureGate>
 * ```
 */
export function FeatureGate({
  featureId,
  children,
  fallback,
  showLoading = true,
  hideWhenLocked = false,
  loadingComponent,
  onLocked,
}: FeatureGateProps) {
  const { isEnabled, isLocked, isLoading, upgradeRequired } = useFeatureGate(featureId);

  // Show loading state
  if (isLoading && showLoading) {
    return <>{loadingComponent || <LoadingSpinner />}</>;
  }

  // Access granted - render children
  if (isEnabled) {
    return <>{children}</>;
  }

  // Access denied
  if (isLocked) {
    // Call the onLocked callback if provided
    if (onLocked) {
      onLocked(featureId, upgradeRequired);
    }

    // Hide completely if requested
    if (hideWhenLocked) {
      return null;
    }

    // Show custom fallback or default upgrade prompt
    if (fallback) {
      return <>{typeof fallback === 'function' ? fallback(upgradeRequired) : fallback}</>;
    }
    return <DefaultUpgradePrompt featureId={featureId} upgradeRequired={upgradeRequired} />;
  }

  // Still loading but showLoading is false - render nothing
  return null;
}

/**
 * FeatureGateInline - Inline version that renders nothing when locked
 * Useful for conditionally showing small UI elements
 *
 * @example
 * ```tsx
 * <FeatureGateInline featureId="advanced_filters">
 *   <AdvancedFiltersButton />
 * </FeatureGateInline>
 * ```
 */
export function FeatureGateInline({
  featureId,
  children,
}: {
  featureId: FeatureId | string;
  children: React.ReactNode;
}) {
  return (
    <FeatureGate featureId={featureId} hideWhenLocked showLoading={false}>
      {children}
    </FeatureGate>
  );
}

/**
 * withFeatureGate - HOC to wrap a component with feature gating
 *
 * @example
 * ```tsx
 * const GatedDealAnalyzer = withFeatureGate(DealAnalyzer, 'deal_analyzer');
 *
 * // Later in JSX
 * <GatedDealAnalyzer />
 * ```
 */
export function withFeatureGate<P extends object>(
  Component: React.ComponentType<P>,
  featureId: FeatureId | string,
  options?: Omit<FeatureGateProps, 'featureId' | 'children'>
) {
  return function FeatureGatedComponent(props: P) {
    return (
      <FeatureGate featureId={featureId} {...options}>
        <Component {...props} />
      </FeatureGate>
    );
  };
}

/**
 * LockedFeatureBadge - Badge to show when a feature is locked
 * Can be used alongside controls to indicate locked state
 *
 * @example
 * ```tsx
 * <div className="flex items-center gap-2">
 *   <ExportButton disabled={isLocked} />
 *   <LockedFeatureBadge featureId="csv_export" />
 * </div>
 * ```
 */
export function LockedFeatureBadge({
  featureId,
  showTier = true,
}: {
  featureId: FeatureId | string;
  showTier?: boolean;
}) {
  const { isLocked, upgradeRequired } = useFeatureGate(featureId);

  if (!isLocked) return null;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">
      <Lock className="w-3 h-3" />
      {showTier && upgradeRequired ? `${upgradeRequired}` : 'Locked'}
    </span>
  );
}

/**
 * useFeatureGateButton - Hook for creating locked/unlocked button states
 *
 * @example
 * ```tsx
 * const { isLocked, props } = useFeatureGateButton('csv_export');
 *
 * return (
 *   <button {...props}>
 *     {isLocked ? 'Upgrade to Export' : 'Export CSV'}
 *   </button>
 * );
 * ```
 */
export function useFeatureGateButton(
  featureId: FeatureId | string,
  onClick?: () => void
): {
  isLocked: boolean;
  isLoading: boolean;
  upgradeRequired: string | null;
  props: {
    disabled: boolean;
    onClick: () => void;
    className: string;
  };
} {
  const { isEnabled, isLocked, isLoading, upgradeRequired } = useFeatureGate(featureId);

  const handleClick = () => {
    if (isEnabled && onClick) {
      onClick();
    }
  };

  return {
    isLocked,
    isLoading,
    upgradeRequired,
    props: {
      disabled: isLocked || isLoading,
      onClick: handleClick,
      className: isLocked ? 'opacity-50 cursor-not-allowed' : '',
    },
  };
}

export default FeatureGate;
